import { Injectable } from '@angular/core';
import { PracticePatientService } from 'src/app/api/proxy/practice/services/practice-patient.service';
import { PractitionerService } from '../practitioner-service/practitioner.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { PromptService } from '../promtp-service/prompt.service';
import {
  EncounterNoteUpdateResponseModel,
  EncounterRegionNoteModel,
  Icd10Model,
  NotesModel,
  PatientCaseModel,
  TreatmentNoteModel,
} from 'src/app/api/proxy/practice/practice-models';
import { PatientOpenCasePost$Params } from 'src/app/api/proxy/practice/fn/patient/patient-open-case-post';
import { PatientCaseIcdUpdatePost$Params } from 'src/app/api/proxy/practice/fn/patient/patient-case-icd-update-post';
import { EncounterNoteUpdatePost$Params } from 'src/app/api/proxy/practice/fn/encounter/encounter-note-update-post';
import { PatientCloseCasePost$Params } from 'src/app/api/proxy/practice/fn/patient/patient-close-case-post';
import { PracticeEncounterService } from 'src/app/api/proxy/practice/services/practice-encounter.service';
import { UserInfoService } from '../auth-service/user-info-service';
import { CaseIcd10Model } from 'src/app/pages/treatment/case/case.component';
import { SecureApiService } from '../authorization/secure-api.service';
import { NetworkService, StoragePrefixType } from '../network-service/network-service';
import { unwrapSoapObjectiveNotes } from 'src/app/components/soap/soap-objective.util';
@Injectable({ providedIn: 'root' })
export class CaseService {
  constructor(
    protected _practicePatientService: PracticePatientService,
    protected _practiceEncounterService: PracticeEncounterService,
    protected _practitionerService: PractitionerService,
    protected _PromptService: PromptService,
    protected _secureService: SecureApiService,
    protected _networkService: NetworkService,
  ) {}

  async fetchCases(patientId: string): Promise<PatientCaseModel[] | null | undefined> {
    const selectedFacility = await this._practitionerService.getSelectedFacility();

    try {
      const cases = await firstValueFrom(
        this._networkService.performNetworkCacheFallbackCall(
          StoragePrefixType.Case,
          patientId,
          this._practicePatientService.patientCaseListPatientIdGet({
            patientId,
          }),
        ),
      );

      return cases ?? null;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(
        error,
        'patient-encounters-details-list',
      );
      return null;
    }
  }

  // Opens a new case for the given patient, then returns the refreshed case list
  async openNewCase(patientId: string): Promise<PatientCaseModel[] | null | undefined> {
    const selectedFacility = await this._practitionerService.getSelectedFacility();
    let body: PatientOpenCasePost$Params = {
      body: {
        facilityId: selectedFacility!.id!,
        patientId: patientId,
        caseOpened: new Date().toISOString(),
      },
    };
    var prac = await UserInfoService.getUserInfo();
    if (prac) {
      body.body!.practitionerId = prac.practitionerId;
    }

    try {
      await firstValueFrom(
        this._secureService.secureCallMessage(
          this._practicePatientService.patientOpenCasePost(body),
        ),
      );

      return await this.fetchCases(patientId);
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'patient-open-case-post');
      return null;
    }
  }

  async closeCase(patientId: string, caseId: string): Promise<PatientCaseModel[] | null> {
    const selectedFacility = await this._practitionerService.getSelectedFacility();

    let req: PatientCloseCasePost$Params = {
      body: {
        facilityId: selectedFacility!.id!,
        patientId: patientId,
        patientCaseId: caseId,
        dateClosed: new Date().toISOString(),
      },
    };

    var prac = await UserInfoService.getUserInfo();
    if (prac) {
      req.body!.practitionerId = prac.practitionerId;
    }

    try {
      const updatedCases = await firstValueFrom(
        this._secureService.secureCallMessage(
          this._practicePatientService.patientCloseCasePost(req),
        ),
      );

      return updatedCases ?? (await this.fetchCases(patientId)) ?? null;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'patient-close-case-post');
      return null;
    }
  }

  // Saves the ICD10 set for a case and returns the updated ICD10 list from the server
  async saveCaseIcd10(
    caseModel: PatientCaseModel,
    icd10?: CaseIcd10Model[] | null,
  ): Promise<Icd10Model[] | null> {
    const selectedFacility = await this._practitionerService.getSelectedFacility();

    const req: PatientCaseIcdUpdatePost$Params = {
      body: {
        facilityId: selectedFacility!.id!,
        patientId: caseModel.patientId!,
        patientCaseId: caseModel.id!,
        icD10Id: icd10!.map((c) => c.id!),
      },
    };

    try {
      return lastValueFrom(
        this._secureService.secureCallMessage(
          this._practicePatientService.patientCaseIcdUpdatePost(req),
        ),
      );
    } catch (err) {
      // optional: handle/log error here
      throw err;
    }
  }

  async encounterSoapNotes(encounterId: string): Promise<NotesModel | null> {
    try {
      const encounter = await firstValueFrom(
        this._practiceEncounterService.encounterIdGet({ id: encounterId }),
      );
      return encounter?.notes ?? null;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'encounter-id-get');
      return null;
    }
  }

  async saveEncounterNote(
    encounterId: string,
    note: NotesModel,
    options?: {
      treatmentNotes?: TreatmentNoteModel[] | null;
      regionNotes?: EncounterRegionNoteModel[] | null;
    },
  ): Promise<EncounterNoteUpdateResponseModel | null> {
    var prac = await UserInfoService.getUserInfo();
    if (prac) {
      note.practitionerFirstName = prac.name;
      note.practitionerLastName = prac.family_name;
      note.practitionerId = prac.practitionerId;
    }

    note.objectiveNotes = unwrapSoapObjectiveNotes(note.objectiveNotes);

    const req: EncounterNoteUpdatePost$Params = {
      body: {
        encounterId,
        note,
        ...(options?.treatmentNotes !== undefined ? { treatmentNotes: options.treatmentNotes } : {}),
        ...(options?.regionNotes !== undefined ? { regionNotes: options.regionNotes } : {}),
      },
    };

    try {
      return await firstValueFrom(
        this._secureService.secureCallMessage(
          this._practiceEncounterService.encounterNoteUpdatePost(req),
        ),
      );
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'encounter-note-update-post');
      return null;
    }
  }

  /** Returns the patient's open case id, creating one if none exists (matches encounter save behavior). */
  async resolveOpenCaseId(patientId: string): Promise<string | null> {
    const cases = await this.fetchCases(patientId);
    const openCase = cases?.find((c) => c.caseOpen);
    if (openCase?.id) {
      return openCase.id;
    }

    const updated = await this.openNewCase(patientId);
    const resolvedCase = updated?.find((c) => c.caseOpen) ?? updated?.[0];
    return resolvedCase?.id ?? null;
  }
}
